import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Sparkles, ArrowRight, Shield, CreditCard } from 'lucide-react';

const FEATURE_COPY = {
  cover_letter: {
    title: 'Cover Letter Generator',
    description: 'AI-powered cover letters tailored to each job description.',
    icon: '  ',
  },
  linkedin_optimize: {
    title: 'LinkedIn Optimizer',
    description: 'AI rewrites your headline and About section for recruiter visibility.',
    icon: '  ',
  },
  interview_prep: {
    title: 'Interview Prep',
    description: 'AI-generated questions and answers based on your resume + target role.',
    icon: '  ',
  },
  ai_rewrite: {
    title: 'AI Bullet Rewrite',
    description: 'Sage rewrites weak resume bullets with quantified impact statements.',
    icon: '  ',
  },
  sage_chat: {
    title: 'Sage AI Coach',
    description: 'Unlimited career coaching conversations powered by your resume context.',
    icon: '  ',
  },
};

export default function FeatureGateModal({ open, onClose, feature, used, limit, period }) {
  const copy = FEATURE_COPY[feature] || { title: 'Premium Feature', description: 'Upgrade to unlock this feature.' };
  const periodLabel = period === 'day' ? 'today' : 'this month';

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="feature-gate-overlay"
        >
          <motion.div
            className="relative bg-white max-w-md w-full shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="feature-gate-modal"
          >
            {/* Header band */}
            <div className="bg-gradient-to-r from-[#002fa7] to-[#001d6a] px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                  <Lock size={18} />
                </div>
                <div>
                  <h2 className="font-satoshi text-lg font-bold" data-testid="feature-gate-title">
                    {copy.title} limit reached
                  </h2>
                  <p className="text-sm text-white/80">
                    You've used {used} of {limit} free {period === 'day' ? 'daily' : 'monthly'} uses
                  </p>
                </div>
              </div>
            </div>

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 text-white/70 hover:text-white transition-colors"
              aria-label="Close"
              data-testid="feature-gate-close"
            >
              <X size={18} />
            </button>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-[#4b5563] mb-5">
                {copy.description} Upgrade to unlock unlimited access {periodLabel === 'today' ? 'every day' : 'every month'}.
              </p>

              {/* What you get */}
              <div className="space-y-2.5 mb-6">
                {[
                  'Unlimited AI rewrites and cover letters',
                  'Full interview prep and LinkedIn optimizer',
                  'Unlimited Sage AI career coaching',
                  'All 12 Resume Studio themes',
                  'Clean exports (no watermark)',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm">
                    <Sparkles size={13} className="text-[#002fa7] flex-shrink-0" />
                    <span className="text-[#0f1115]">{item}</span>
                  </div>
                ))}
              </div>

              {/* Price anchor */}
              <div className="bg-[#f4f5f7] border border-[#e5e7eb] p-4 mb-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#6b7280] uppercase tracking-wider font-bold">Founding Member Price</p>
                    <p className="font-satoshi text-2xl font-black text-[#0f1115]">
                      $4.50<span className="text-sm font-normal text-[#6b7280]">/mo</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs line-through text-[#9ca3af]">$39/mo</p>
                    <span className="inline-block bg-[#10b981] text-white text-xs font-bold px-2 py-0.5">
                      SAVE 40%
                    </span>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <Link
                to="/pricing?ref=feature_gate&feature={feature}&utm_source=in_app&utm_medium=feature_gate&utm_campaign=conversion"
                className="w-full bg-[#002fa7] text-white font-bold py-3 hover:bg-[#001f6f] transition-colors inline-flex items-center justify-center gap-2 mb-3"
                data-testid="feature-gate-upgrade-btn"
                onClick={onClose}
              >
                Upgrade now <ArrowRight size={16} />
              </Link>
              <button
                onClick={onClose}
                className="w-full text-sm text-[#6b7280] hover:text-[#0f1115] py-2 transition-colors"
                data-testid="feature-gate-dismiss"
              >
                Maybe later
              </button>

              {/* Trust signals */}
              <div className="mt-4 pt-3 border-t border-[#e5e7eb] flex items-center justify-center gap-4 text-[10px] text-[#9ca3af]">
                <span className="flex items-center gap-1"><Shield size={10} /> Secured by Stripe</span>
                <span className="flex items-center gap-1"><CreditCard size={10} /> Cancel anytime</span>
                <span className="flex items-center gap-1"><Sparkles size={10} /> 14-day trial</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
